// modules/WorldInteraction/WorldObjectInteraction.js
// 世界场景物体交互模块 - 处理推动物体、拾取等物体交互

import { GAME_CONSTANTS } from '../GameConstants.js';
import DebugHelper from '../DebugHelper.js';
import GameStateStore from '../GameStateStore.js';
import { playSceneSfx } from '../LazyAssetLoader.js';

export default class WorldObjectInteraction {
    constructor(scene) {
        this.scene = scene;
    }
    
    /**
     * 尝试推动物体
     * @param {number} playerX - 玩家X坐标
     * @param {number} playerY - 玩家Y坐标
     * @param {string} direction - 玩家面向方向
     * @returns {boolean} 是否成功推动
     */
    tryPushObject(playerX, playerY, direction) {
        if (!this.scene.worldMap || !this.scene.worldMap.pushableObjects) {
            return false;
        }
        
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        
        // 根据方向计算玩家面前的位置
        let frontX = playerX;
        let frontY = playerY;
        
        switch (direction) {
            case 'up':
                frontY -= tileSize;
                break;
            case 'down':
                frontY += tileSize;
                break;
            case 'left':
                frontX -= tileSize;
                break;
            case 'right':
                frontX += tileSize;
                break;
            default:
                return false;
        }
        
        // 查找玩家面前位置的可推动物体
        let pushableObject = null;
        const checkRadius = tileSize / 2; // 检查半径
        
        for (const obj of this.scene.worldMap.pushableObjects) {
            if (!obj.active || !obj.getData('isPushable')) {
                continue;
            }
            
            // 检查物体是否在玩家面前的位置
            const distance = Phaser.Math.Distance.Between(
                frontX, frontY,
                obj.x, obj.y
            );
            
            if (distance < checkRadius) {
                pushableObject = obj;
                break; // 找到就退出
            }
        }
        
        if (!pushableObject) {
            return false;
        }
        
        // 计算目标位置（物体被推动后的位置）
        let targetX = pushableObject.x;
        let targetY = pushableObject.y;
        
        switch (direction) {
            case 'up':
                targetY -= tileSize;
                break;
            case 'down':
                targetY += tileSize;
                break;
            case 'left':
                targetX -= tileSize;
                break;
            case 'right':
                targetX += tileSize;
                break;
        }
        
        // 检查目标位置是否有障碍物
        if (this.isPositionBlocked(targetX, targetY)) {
            this.scene.worldUI.showMessage('无法推动，前方有障碍物');
            return true; // 返回true表示已经处理了交互，只是失败了
        }
        
        // 推动物体
        this.pushObject(pushableObject, targetX, targetY);
        return true;
    }
    
    /**
     * 检查位置是否被阻挡
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @returns {boolean} 是否被阻挡
     */
    isPositionBlocked(x, y) {
        if (!this.scene.worldMap || !this.scene.worldMap.obstacles) {
            return false;
        }
        
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        const checkRadius = tileSize / 2 - 2; // 检查半径，稍微小一点避免边界问题
        
        // 检查所有障碍物
        const obstacles = this.scene.worldMap.obstacles.getChildren();
        for (const obstacle of obstacles) {
            if (!obstacle.active) continue;
            
            const distance = Phaser.Math.Distance.Between(x, y, obstacle.x, obstacle.y);
            if (distance < checkRadius) {
                // 如果是可推动物体，也算作障碍（不能推到另一个可推动物体上）
                return true;
            }
        }
        
        // 检查其他可推动物体（不能推到另一个可推动物体上）
        if (this.scene.worldMap.pushableObjects) {
            for (const pushableObj of this.scene.worldMap.pushableObjects) {
                if (!pushableObj.active) continue;
                
                const distance = Phaser.Math.Distance.Between(x, y, pushableObj.x, pushableObj.y);
                if (distance < checkRadius) {
                    return true;
                }
            }
        }
        
        // 检查NPC
        if (this.scene.worldNPC) {
            const npcs = this.scene.worldNPC.getAllNPCs();
            for (const npc of npcs) {
                if (!npc.active) continue;
                
                const distance = Phaser.Math.Distance.Between(x, y, npc.x, npc.y);
                if (distance < checkRadius) {
                    return true;
                }
            }
        }
        
        // 检查玩家
        if (this.scene.playerSprite && this.scene.playerSprite.active) {
            const distance = Phaser.Math.Distance.Between(x, y, this.scene.playerSprite.x, this.scene.playerSprite.y);
            if (distance < checkRadius) {
                return true;
            }
        }
        
        return false;
    }
    
    /**
     * 推动物体
     * @param {Phaser.GameObjects.Sprite} obj - 要推动的物体
     * @param {number} targetX - 目标X坐标
     * @param {number} targetY - 目标Y坐标
     */
    pushObject(obj, targetX, targetY) {
        // 检查目标位置是否是出口
        const exitInfo = this.isPositionExit(targetX, targetY);
        if (exitInfo) {
            // 获取物体名称（用于事件触发）
            // 优先使用name数据，如果没有则使用texture key（去掉"自然_"前缀）
            let objName = obj.getData('name');
            if (!objName || objName.startsWith('自然_')) {
                objName = obj.texture?.key || obj.texture || '未知物体';
            }
            
            // 播放掉落动画，并传递出口信息
            this.playFallAnimation(obj, targetX, targetY, exitInfo, objName);
            // 播放掉落音效
            playSceneSfx(this.scene, 'fall');
            return;
        }
        
        // 使用tween实现平滑移动
        this.scene.tweens.add({
            targets: obj,
            x: targetX,
            y: targetY,
            duration: 200, // 200ms移动时间
            ease: 'Power2',
            onComplete: () => {
                // 更新物体的碰撞体位置
                if (obj.body) {
                    // 使用 updateFromGameObject 来同步物理体位置
                    obj.body.updateFromGameObject();
                }
            }
        });
        
        // 播放推动音效（如果有的话）
        // this.scene.sound.play('push');
    }
    
    /**
     * 检查位置是否是出口
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @returns {Object|null} 匹配的出口信息，如果不是出口则返回null
     */
    isPositionExit(x, y) {
        if (!this.scene.worldMapJson || !this.scene.worldMapJson.exits) {
            return null;
        }
        
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        const exits = this.scene.worldMapJson.exits;
        
        for (const exit of exits) {
            // 计算出口区域的像素坐标
            const exitX = exit.tileX * tileSize + (exit.tileWidth * tileSize) / 2;
            const exitY = exit.tileY * tileSize + (exit.tileHeight * tileSize) / 2;
            const exitWidth = exit.tileWidth * tileSize;
            const exitHeight = exit.tileHeight * tileSize;
            
            // 检查点是否在出口区域内
            const left = exitX - exitWidth / 2;
            const right = exitX + exitWidth / 2;
            const top = exitY - exitHeight / 2;
            const bottom = exitY + exitHeight / 2;
            
            if (x >= left && x <= right && y >= top && y <= bottom) {
                return exit; // 返回匹配的出口信息
            }
        }
        
        return null;
    }
    
    /**
     * 播放掉落动画
     * @param {Phaser.GameObjects.Sprite} obj - 要掉落的物体
     * @param {number} targetX - 目标X坐标
     * @param {number} targetY - 目标Y坐标
     * @param {Object} exitInfo - 出口信息（可选）
     * @param {string} objName - 物体名称（可选）
     */
    playFallAnimation(obj, targetX, targetY, exitInfo = null, objName = null) {
        // 先移动到目标位置
        this.scene.tweens.add({
            targets: obj,
            x: targetX,
            y: targetY,
            duration: 200,
            ease: 'Power2',
            onComplete: () => {
                // 如果有出口信息，记录跨场景事件触发信息
                if (exitInfo && exitInfo.targetScene && exitInfo.targetTileX !== undefined && exitInfo.targetTileY !== undefined) {
                    this.recordCrossSceneEvent(exitInfo, objName);
                }
                
                // 到达位置后，播放掉落动画
                // 从大到小，同时稍微向下移动，然后消失
                const fallDistance = 30; // 掉落时向下移动的距离
                
                // 确保物体有缩放属性
                if (obj.scaleX === undefined) {
                    obj.setScale(1);
                }
                
                // 掉落动画：缩放从1到0，同时向下移动，使用更快的缓动效果
                this.scene.tweens.add({
                    targets: obj,
                    scaleX: 0,
                    scaleY: 0,
                    y: targetY + fallDistance,
                    duration: 500, // 掉落动画时长（稍微长一点让效果更明显）
                    ease: 'Power3', // 使用Power3让掉落更快
                    onComplete: () => {
                        // 动画完成后，隐藏物体并移除碰撞
                        obj.setVisible(false);
                        obj.setActive(false);
                        
                        // 从障碍物组中移除
                        if (obj.body && this.scene.worldMap && this.scene.worldMap.obstacles) {
                            this.scene.worldMap.obstacles.remove(obj, true, true);
                        }
                        
                        // 从可推动物体列表中移除
                        if (this.scene.worldMap && this.scene.worldMap.pushableObjects) {
                            const index = this.scene.worldMap.pushableObjects.indexOf(obj);
                            if (index > -1) {
                                this.scene.worldMap.pushableObjects.splice(index, 1);
                            }
                        }
                        
                        // 可选：播放掉落音效
                        // this.scene.sound.play('fall');
                    }
                });
            }
        });
    }
    
    /**
     * 记录跨场景事件触发信息
     * @param {Object} exitInfo - 出口信息
     * @param {string} objName - 物体名称
     */
    recordCrossSceneEvent(exitInfo, objName) {
        if (!GameStateStore.getState()) {
            DebugHelper.warn('WorldObjectInteraction: gameState不存在，无法记录跨场景事件');
            return;
        }

        const eventInfo = {
            triggerName: objName,
            targetScene: exitInfo.targetScene,
            triggerX: exitInfo.targetTileX,
            triggerY: exitInfo.targetTileY,
            timestamp: Date.now()
        };

        GameStateStore.pushTriggeredEvent(eventInfo);
        DebugHelper.log('WorldObjectInteraction: 记录跨场景事件', eventInfo);
    }
}

